import { useEffect, useState } from "react";
import axios from "axios";
import { logAuditEvent, AuditChange } from "../../../api/api-hook";
import { getAdminSession } from "../../../utils/adminAuth";

const HOST = process.env.REACT_APP_HOST_URL || "";
const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

interface PageEntry {
  key: string;
  label: string;
  title: string;
  overview: string;
}

export default function WebPageContent() {
  const [pages, setPages]         = useState<PageEntry[]>([]);
  const [loading, setLoading]     = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft]         = useState<{ title: string; overview: string }>({ title: "", overview: "" });
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState<string | null>(null);
  const [error, setError]         = useState("");

  useEffect(() => {
    axios.get(`${HOST}/api/page-content`)
      .then((r) => { if (r.data.success) setPages(r.data.content || []); })
      .finally(() => setLoading(false));
  }, []);

  const startEdit = (page: PageEntry) => {
    setEditingKey(page.key);
    setDraft({ title: page.title, overview: page.overview });
    setError("");
    setSaved(null);
  };

  const cancelEdit = () => { setEditingKey(null); setError(""); };

  const save = async (key: string) => {
    if (!draft.title.trim()) return setError("Title is required.");
    setSaving(true);
    setError("");
    try {
      const updated = pages.map((p) => p.key === key ? { ...p, ...draft } : p);
      const { data } = await axios.put(`${HOST}/api/admin/page-content`, { content: updated });
      if (!data.success) return setError(data.message || "Failed to save.");
      const oldPage = pages.find((p) => p.key === key);
      const contentChanges: AuditChange[] = [
        oldPage?.title    !== draft.title    ? { field: "Title",    oldValue: oldPage?.title    || "—", newValue: draft.title    || "—" } : null,
        oldPage?.overview !== draft.overview ? { field: "Overview", oldValue: oldPage?.overview || "—", newValue: draft.overview || "—" } : null,
      ].filter((c): c is AuditChange => c !== null);
      setPages(updated);
      setEditingKey(null);
      setSaved(key);
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "page_content",
        label:   "Page Content Updated",
        details: `${key}`,
        changes: contentChanges.length > 0 ? contentChanges : undefined,
      });
      setTimeout(() => setSaved(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || "An error occurred.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h4 style={{ color: BRAND_GREEN, fontWeight: 700, margin: 0 }}>Web Page Content</h4>
        <p className="text-muted small mb-0 mt-1">
          Update the heading and overview text displayed on each step of the registration flow.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {pages.map((page) => {
            const isEditing = editingKey === page.key;
            return (
              <div
                key={page.key}
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  border: `1px solid ${isEditing ? BRAND_GREEN : "#e9ecef"}`,
                  overflow: "hidden",
                  transition: "border-color 0.15s",
                }}
              >
                {/* Step header bar */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0.75rem 1.25rem",
                    backgroundColor: isEditing ? BRAND_GREEN : "#f8f9fa",
                    borderBottom: `1px solid ${isEditing ? "rgba(255,255,255,0.15)" : "#e9ecef"}`,
                    transition: "background-color 0.15s",
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: isEditing ? BRAND_GOLD : "#6c757d",
                      }}
                    >
                      {page.label}
                    </span>
                    {saved === page.key && (
                      <span style={{ fontSize: "0.75rem", color: "#198754", fontWeight: 600 }}>
                        <i className="bi bi-check-circle-fill me-1" />Saved
                      </span>
                    )}
                  </div>
                  {!isEditing && (
                    <button
                      className="btn btn-sm"
                      style={{
                        backgroundColor: "rgba(2,56,34,0.08)",
                        border: "none",
                        color: BRAND_GREEN,
                        fontSize: "0.8rem",
                        padding: "0.2rem 0.6rem",
                        borderRadius: 6,
                        fontWeight: 600,
                      }}
                      onClick={() => startEdit(page)}
                    >
                      <i className="bi bi-pencil-fill me-1" />Edit
                    </button>
                  )}
                </div>

                {/* Content */}
                <div style={{ padding: "1.1rem 1.25rem" }}>
                  {isEditing ? (
                    <div>
                      {error && <div className="alert alert-danger py-2 small mb-3">{error}</div>}

                      <div className="mb-3">
                        <label className="form-label small fw-semibold">
                          Page Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={draft.title}
                          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                          placeholder="Page heading"
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label small fw-semibold">Overview / Description</label>
                        <textarea
                          className="form-control"
                          rows={4}
                          value={draft.overview}
                          onChange={(e) => setDraft((d) => ({ ...d, overview: e.target.value }))}
                          placeholder="Short paragraph shown below the heading (leave blank to hide)"
                          style={{ resize: "vertical" }}
                        />
                        <div className="form-text">Leave blank to hide the overview paragraph on this step.</div>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm"
                          style={{ backgroundColor: BRAND_GREEN, color: "#fff", fontWeight: 600, minWidth: 90 }}
                          onClick={() => save(page.key)}
                          disabled={saving}
                        >
                          {saving
                            ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
                            : "Save"}
                        </button>
                        <button
                          className="btn btn-sm btn-light"
                          onClick={cancelEdit}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2">
                        <span className="text-muted small fw-semibold me-2">Title</span>
                        <span style={{ fontWeight: 600, color: "#212529" }}>{page.title || <span className="text-muted fst-italic">—</span>}</span>
                      </div>
                      <div>
                        <span className="text-muted small fw-semibold me-2">Overview</span>
                        {page.overview
                          ? <span className="small" style={{ color: "#495057" }}>{page.overview}</span>
                          : <span className="text-muted small fst-italic">Hidden (blank)</span>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
